import { useState, useEffect, useCallback } from 'react';
import {
  USER_SESSION_KEY,
  authenticateWithWallet,
  checkAuthentication,
  getStoredRadcoonSession,
  logoutRadcoonSession,
} from '../services/auth';

/**
 * Hook for managing authentication state
 * @param {Object} walletInfo - Information about the user's wallet from useRadixConnect
 * @returns {Object} Auth state and methods
 */
export function useAuth(walletInfo = {}) {
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { connected, address, rdt, autoLogin = true } = walletInfo;
  
  // Try to load user from sessionStorage on initialization
  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem(USER_SESSION_KEY);
      const storedSession = getStoredRadcoonSession();

      if (storedSession) {
        console.log('Loaded secure Radcoon session from session storage:', storedSession.user);
        setUser(storedSession.user);
        setUserId(storedSession.user.id);
        setSessionToken(storedSession.sessionToken);
        setIsLoading(false);
      } else if (storedUser) {
        sessionStorage.removeItem(USER_SESSION_KEY);
      }
    } catch (e) {
      console.error('Error loading user from session storage:', e);
    }
  }, []);
  
  // Authenticate with wallet
  const login = useCallback(async (walletAddress, walletRdt = rdt) => {
    if (!walletAddress) {
      console.error('Login attempted with no wallet address');
      setError('No wallet address provided');
      setIsLoading(false);
      return null;
    }
    
    console.log('Attempting login with wallet:', walletAddress);
    setIsLoading(true);
    setError(null);
    
    try {
      const { user, sessionToken: nextSessionToken, error, isNewUser } =
        await authenticateWithWallet(walletAddress, walletRdt);
      
      if (error) {
        console.error('Authentication error:', error);
        setError(error.message || 'Authentication failed');
        setIsLoading(false);
        return null;
      }
      
      // Save user in state
      setUser(user);
      setUserId(user.id);
      setSessionToken(nextSessionToken);
      
      // Save user in session storage for persistence
      try {
        sessionStorage.setItem(USER_SESSION_KEY, JSON.stringify(user));
      } catch (e) {
        console.error('Error saving user to session storage:', e);
      }
      
      console.log('Authentication successful. User ID:', user.id, 'Is new user:', isNewUser);
      
      setIsLoading(false);
      return user;
    } catch (e) {
      console.error('Unexpected error during authentication:', e);
      setError(e.message || 'Unexpected error during authentication');
      setIsLoading(false);
      return null;
    }
  }, [rdt]);
  
  // Logout function
  const logout = useCallback(async () => {
    await logoutRadcoonSession();
    setUser(null);
    setUserId(null);
    setSessionToken(null);
    setError(null);
  }, []);
  
  // Check session on mount and when wallet connection changes
  useEffect(() => {
    const checkSession = async () => {
      setIsLoading(true);
      
      const storedSession = getStoredRadcoonSession();
      
      if (storedSession && !address) {
        setUser(storedSession.user);
        setUserId(storedSession.user.id);
        setSessionToken(storedSession.sessionToken);
        setIsLoading(false);
        return;
      }

      if (storedSession && storedSession.walletAddress === address) {
        const { authenticated, user, sessionToken: activeSessionToken } =
          await checkAuthentication(storedSession.user.id);
        
        if (authenticated && user) {
          setUser(user);
          setUserId(user.id);
          setSessionToken(activeSessionToken);
          setIsLoading(false);
          return;
        }
        
        // Clear invalid session
        await logout();
      } else if (storedSession && connected && address && storedSession.walletAddress !== address) {
        await logout();
      }
      
      // If we're connected with a wallet, try to authenticate
      if (connected && address && rdt && autoLogin) {
        login(address, rdt);
      } else {
        if (!connected && !storedSession) {
          await logout();
        }
        setIsLoading(false);
      }
    };
    
    checkSession();
  }, [connected, address, rdt, autoLogin, login, logout]);
  
  // Store user ID in localStorage when it changes
  useEffect(() => {
    if (userId) {
      localStorage.setItem('radcoon_user_id', userId);
    } else {
      localStorage.removeItem('radcoon_user_id');
    }
  }, [userId]);
  
  return {
    user,
    userId,
    isLoading,
    error,
    login,
    logout,
    sessionToken,
    isAuthenticated: !!user && !!sessionToken
  };
} 
